import { LinkPermissionOptionLabels } from '@components/App/Modal/Share/Share'
import { Select } from '@lib/components'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import { managerActions } from '@store/reducers/manager/actions'
import {
  NotificationType,
  notificationActionCreators,
} from '@storeOld/reducers/notification'
import { openModal } from '@storeOld/reducers/ui'
import type React from 'react'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { GtmTriggerName, sendGtmEvent } from '@/constants/gtm'
import { SHARE_PERMISSION } from '@/permissions/project/definitions'
import API from '../../../../../../../../../../api'
import { useProjectAccess } from '../../../../../../../../../../hooks'
import { MODAL_UPGRADE_PLAN } from '../../../../../../../constants'
import { useGetLinkPermissionOptions } from '../../hooks/useGetLinkPermissionOptions'
import {
  type PROJECT_LINK_PERMISSION_TYPE,
  PROJECT_SHARE_TYPE,
} from '../../types'

const LinkPermissionSettings: React.FC = () => {
  const dispatch = useDispatch()

  const linkPermissionOptions = useGetLinkPermissionOptions()

  const projectId = useSelector(migrationProjectSelector.getProjectId)

  const shareType = useSelector(migrationProjectSelector.getProjectShareType)

  const linkPermission = useSelector(
    migrationProjectSelector.getProjectLinkPermission
  )

  const [selectedLinkPermissionOption, setSelectedLinkPermissionOption] =
    useState<PROJECT_LINK_PERMISSION_TYPE | LinkPermissionOptionLabels>(
      linkPermission
    )

  useEffect(() => {
    setSelectedLinkPermissionOption(linkPermission)
  }, [linkPermission])

  const changeLinkPermissionType = async (
    linkPermissionType:
      | PROJECT_LINK_PERMISSION_TYPE
      | LinkPermissionOptionLabels
  ) => {
    try {
      const response = await API.Projects.linkPermission({
        projectId,
        linkPermission: linkPermissionType,
      })

      if (!response.ok) {
        dispatch(
          notificationActionCreators.setNotification({
            type: NotificationType.SOMETHING_WENT_WRONG_ERROR,
          })
        )

        return
      }

      const data = await response.json()

      dispatch(managerActions.updateMeta(data))

      dispatch(
        notificationActionCreators.setNotification({
          type: NotificationType.ACCESS_SETTINGS_UPDATED,
        })
      )
    } catch (error) {
      // biome-ignore lint/suspicious/noConsole: intentional
      console.log(error)

      dispatch(
        notificationActionCreators.setNotification({
          type: NotificationType.SOMETHING_WENT_WRONG_ERROR,
        })
      )
    }
  }

  const handleOnChangeLinkPermissionOption = (value: string | number) => {
    const currentOption = linkPermissionOptions.find(
      (option) => option.value === value
    )

    if (!currentOption?.disabled) {
      setSelectedLinkPermissionOption(value as PROJECT_LINK_PERMISSION_TYPE)
      changeLinkPermissionType(value as PROJECT_LINK_PERMISSION_TYPE)
      return
    }

    if (currentOption.badgeType === 'plan_team') {
      sendGtmEvent(GtmTriggerName.UPGRADE_GUEST_ONLY)

      dispatch(
        openModal({
          modalType: MODAL_UPGRADE_PLAN,
          modalProps: {
            planType: 'team',
            slideType: 'comments',
          },
        })
      )
    }
  }

  const { hasAccess } = useProjectAccess(
    SHARE_PERMISSION.CHANGE_SHARE_SETTINGS_TYPE
  )

  if (shareType === PROJECT_SHARE_TYPE.ONLY_MEMBERS) {
    return null
  }

  return (
    <Select
      options={linkPermissionOptions}
      type="select"
      selected={
        selectedLinkPermissionOption || LinkPermissionOptionLabels.CAN_VIEW
      }
      disabled={!hasAccess}
      onChange={handleOnChangeLinkPermissionOption}
      disableScrollIntoView
      listMinWidth={160}
      position="bottom-end"
    />
  )
}

export default LinkPermissionSettings
